import type { Monaco } from "@monaco-editor/react";
import type { languages, IDisposable } from "monaco-editor";
import {
  getCompletionContext,
  getSuggestions,
  type CompletionSuggestion,
  type SuggestionKind,
} from "./sqlCompletionCore";

export type { AutoJoin, TableOrView, FunctionInfo, ForeignKey, SchemaInfo } from "./sqlCompletionCore";

export function toMonacoKind(kind: SuggestionKind, CompletionItemKind: typeof languages.CompletionItemKind): languages.CompletionItemKind {
  switch (kind) {
    case "schema": return CompletionItemKind.Module;
    case "table": return CompletionItemKind.Class;
    case "view": return CompletionItemKind.Interface;
    case "column": return CompletionItemKind.Field;
    case "calculatedField": return CompletionItemKind.Property;
    case "autoJoin": return CompletionItemKind.Module;
    case "fkJoin": return CompletionItemKind.Reference;
    case "function": return CompletionItemKind.Function;
    case "keyword": return CompletionItemKind.Keyword;
  }
}

export function registerSqlCompletionProvider(
  monaco: Monaco,
  schemaInfoRef: { current: import("./sqlCompletionCore").SchemaInfo | null },
  opts?: {
    /**
     * Tables in scope without a FROM clause (bare-expression editors — a
     * calculated field, a row filter). See getSuggestions.implicitTables.
     */
    implicitTablesRef?: { current: string[] };
    /** always-valid alias -> table-name bindings (e.g. outer, the table name) */
    extraAliasesRef?: { current: Record<string, string> };
    /** when true, general context offers only aliases + keywords, no bare cols */
    suppressBareColumnsRef?: { current: boolean };
    /** input columns offered after `@` (validation queries) */
    atFieldColumnsRef?: { current: string[] };
  },
): IDisposable {
  return monaco.languages.registerCompletionItemProvider("sql", {
    // `@` triggers input-field completion for validation queries.
    triggerCharacters: [".", " ", "\n", "@"],
    provideCompletionItems(model, position) {
      const schema = schemaInfoRef.current;
      if (!schema) return { suggestions: [] };

      const fullText = model.getValue();
      const offset = model.getOffsetAt(position);

      const textUntilPosition = model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });
      const lineText = model.getLineContent(position.lineNumber);
      const lineTextBeforeCursor = lineText.substring(0, position.column - 1);

      const ctx = getCompletionContext(lineTextBeforeCursor, textUntilPosition);
      const coreSuggestions = getSuggestions(
        ctx,
        schema,
        fullText,
        offset,
        opts?.implicitTablesRef?.current,
        opts?.extraAliasesRef?.current,
        opts?.suppressBareColumnsRef?.current,
        opts?.atFieldColumnsRef?.current,
      );

      const CompletionItemKind = monaco.languages.CompletionItemKind;

      // Compute the replacement range
      // For dot context: replace from after the last dot to cursor
      // For other contexts: replace the current word
      let startColumn: number;
      if (ctx.kind === "dot") {
        startColumn = position.column - ctx.prefix.length;
      } else {
        const word = model.getWordUntilPosition(position);
        startColumn = word.startColumn;
      }

      const range = {
        startLineNumber: position.lineNumber,
        startColumn,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      };

      const suggestions: languages.CompletionItem[] = coreSuggestions.map((s: CompletionSuggestion) => ({
        label: s.label,
        kind: toMonacoKind(s.kind, CompletionItemKind),
        detail: s.detail,
        insertText: s.insertText,
        range,
        sortText: s.sortText,
      }));

      return { suggestions };
    },
  });
}
